// PR #236 review (CQ Minor): 검색 필터 helper 추출.
// DealerIssuePanel / LicenseSubsidiaryTab 의 12라인 복제 로직을 DRY 화.
//
// 검색 대상: userid/userId(소스 경계) / groupName / email (대소문자 무시, 부분 일치).
// 측정 실패 시 해당 필드는 빈 문자열로 간주 — 크래시 없이 처리.

import { getLicenseUserId, isRenewalLicense, isExpired } from './licenseHelper';
import { LICENSE_STATUS_EXPIRED, LICENSE_TYPE_INITIAL, LICENSE_TYPE_RENEWAL } from '../config/appConstants';

/**
 * 라이선스 목록에서 검색어와 부분 일치하는 항목만 반환.
 *
 * @param {Array} licenses - 필터링할 라이선스 배열
 * @param {string} searchText - 검색어 (빈 문자열이면 전체 반환)
 * @returns {Array} 매칭된 라이선스 배열
 */
export function filterCustomerLicenses(licenses, searchText) {
    if (!Array.isArray(licenses)) return [];
    const q = (searchText || '').trim().toLowerCase();
    if (!q) return licenses;
    return licenses.filter((lic) => {
        const userid = (getLicenseUserId(lic) || '').toLowerCase();
        const groupName = (lic.groupName || '').toLowerCase();
        const email = (lic.email || '').toLowerCase();
        return userid.includes(q) || groupName.includes(q) || email.includes(q);
    });
}

const inRange = (value, range) => {
    if (range?.fromTs === undefined || range?.toTs === undefined) return true;
    return typeof value === 'number' && value >= range.fromTs && value <= range.toTs;
};

/**
 * 고객 라이선스 화면 필터 체인 (유형 -> 만료 상태 -> 발급 기간 -> 만료 기간 -> 검색).
 * DealerIssuePanel / LicenseIssuanceTab 공용. 범위는 fromTs/toTs 둘 다 있을 때만 적용(경계 포함).
 *
 * @param {Array} licenses
 * @param {{type?: string, status?: string, periodRange?: {fromTs?: number, toTs?: number},
 *          expiresRange?: {fromTs?: number, toTs?: number}, search?: string}} filters
 * @returns {Array}
 */
export function applyCustomerLicenseFilters(licenses, filters) {
    if (!Array.isArray(licenses)) return [];
    const { type, status, periodRange, expiresRange, search } = filters || {};
    let result = licenses;
    if (type === LICENSE_TYPE_RENEWAL || type === LICENSE_TYPE_INITIAL) {
        result = result.filter((lic) => (isRenewalLicense(lic) ? type === LICENSE_TYPE_RENEWAL : type === LICENSE_TYPE_INITIAL));
    }
    if (status === LICENSE_STATUS_EXPIRED) {
        result = result.filter((lic) => isExpired(lic));
    }
    result = result.filter((lic) => inRange(lic?.created, periodRange));
    result = result.filter((lic) => inRange(lic?.expired, expiresRange));
    return filterCustomerLicenses(result, search);
}

/**
 * 발급 이력(QR) 유형/상태 필터. DealerIssuePanel / LicenseIssuanceTab 공용.
 * type: 'initial' | 'renewal' (그 외 = 전체), status: 'pending' | 'activated' (그 외 = 전체).
 * 유형은 isRenewalLicense SSoT(SK 5번째 segment 우선), 상태는 used > 0 이면 활성화.
 *
 * @param {Array} items
 * @param {{type?: string, status?: string}} filters
 * @returns {Array}
 */
export function filterIssueHistory(items, filters) {
    if (!Array.isArray(items)) return [];
    const { type, status } = filters || {};
    const byType = type === LICENSE_TYPE_INITIAL || type === LICENSE_TYPE_RENEWAL;
    const byStatus = status === 'pending' || status === 'activated';
    if (!byType && !byStatus) return items;
    return items.filter((qr) => {
        if (byType && (isRenewalLicense(qr) ? LICENSE_TYPE_RENEWAL : LICENSE_TYPE_INITIAL) !== type) return false;
        if (byStatus && ((qr?.used ?? 0) > 0 ? 'activated' : 'pending') !== status) return false;
        return true;
    });
}
